import { useEffect, useState } from 'react'
import type { ProductVariant, User } from '../../../lib/types'
import { getRankPercent } from '../../../lib/rank'

export function useDiscounts() {
  const USER_DISCOUNT_CAP_PERCENT = 5
  const DEFAULT_CTV_DISCOUNT_PERCENT = 5

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(false)
  }, [])

  const rankPercent = (rank?: User['rank']) => getRankPercent(rank)

  const computePercent = (user: User | null | undefined, variant: ProductVariant) => {
    const originalPrice = Math.max(0, Number(variant.price || 0))
    const variantDiscount = 0 // discount_percent now managed at supplier level (variant_supplier_prices)
    const rankDiscount = Math.max(0, Math.min(100, rankPercent(user?.rank)))
    const ctvDiscount = user?.is_ctv ? DEFAULT_CTV_DISCOUNT_PERCENT : 0
    const rawUserDiscount = rankDiscount + ctvDiscount
    const userDiscount = Math.min(rawUserDiscount, USER_DISCOUNT_CAP_PERCENT)

    const productBasedPrice = Math.round(originalPrice * (100 - variantDiscount) / 100)
    const userBasedPrice = Math.round(originalPrice * (100 - userDiscount) / 100)

    const selectedSource =
      variantDiscount <= 0 && userDiscount <= 0
        ? 'none'
        : productBasedPrice <= userBasedPrice
          ? 'product'
          : 'user'

    const integratedPercent = selectedSource === 'product'
      ? variantDiscount
      : selectedSource === 'user'
        ? userDiscount
        : 0

    const finalUnitPrice = selectedSource === 'product'
      ? productBasedPrice
      : selectedSource === 'user'
        ? userBasedPrice
        : originalPrice

    return {
      integratedPercent,
      totalPercentAppliedToPrice: integratedPercent,
      finalUnitPrice,
      breakdown: {
        variantDiscount,
        rankDiscount,
        ctvDiscount,
        rawUserDiscount,
        userDiscount,
        cappedAtLimit: rawUserDiscount > USER_DISCOUNT_CAP_PERCENT,
        productBasedPrice,
        userBasedPrice,
        selectedSource,
      }
    }
  }

  const getUnitPrice = (user: User | null | undefined, variant: ProductVariant) => {
    const { finalUnitPrice } = computePercent(user, variant)
    return finalUnitPrice
  }

  return {
    loading,
    computePercent,
    getUnitPrice,
  }
}
